import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { searchPatch, optionalSearch } from "@/lib/search";
import { SearchField } from "@/components/app/patterns/search-field";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  THERAPY_DISCIPLINES,
  THERAPY_NOTE_SECTION_KEYS,
  THERAPY_NOTE_SECTION_LABELS,
  errorWords,
} from "@alm/domain";
import { RecordCard } from "@/components/app/lists/record-card";
import { EmptyRows } from "@/components/app/lists/empty-rows";
import { RecordRow } from "@/components/app/lists/record-row";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RowActions } from "@/components/app/patterns/row-actions";
import { SharedRowNote } from "@/components/app/lists/shared-row-note";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useBuildingName } from "@/hooks/use-building-name";
import { useNewRowPlace } from "@/hooks/use-new-row-place";
import { orpc } from "@/lib/orpc";
import { useCan, useCanChangeRow } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { NoMatches } from "@/routes/_authenticated/_administration/settings/-components/no-matches";
import { RowsSkeleton } from "@/routes/_authenticated/_administration/settings/-components/rows-skeleton";
import {
  TherapyNoteTemplateForm,
  type TherapyNoteTemplateRow,
} from "./-components/care/therapy-note-template-form";

export const Route = createFileRoute(
  "/_authenticated/_administration/settings/therapy-note-templates",
)({
  validateSearch: (search: Record<string, unknown>): { q?: string; retired?: true } => ({
    q: optionalSearch(search.q),
    retired: search.retired === true ? true : undefined,
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "therapy-note-templates"),
  component: TherapyNoteTemplatesSettings,
});

const ANY = "all";
const STANDARD = "A standard template every operator starts with, so it cannot be changed here.";
const fail = (error: Error) => toast.error(errorWords(error));
const disciplineLabel = (value: string | null) =>
  value
    ? (THERAPY_DISCIPLINES.find((row) => row.value === value)?.label ?? value)
    : "Any discipline";

/** What the template fills; a section this application does not offer is named as stored, not
 * hidden. */
const sectionLabel = (template: TherapyNoteTemplateRow) =>
  template.sectionKey
    ? (THERAPY_NOTE_SECTION_LABELS[template.sectionKey] ?? template.sectionKey)
    : "Whole note";

/** Templates are the operator's, not one building's, so a therapist meets the same opening
 * anywhere. */
function TherapyNoteTemplatesSettings() {
  const canManage = useCan("therapy:manage");
  // A template is changed at its building, one for every building only organization-wide, and a
  // standard one (the platform's) by nobody here.
  const canChange = useCanChangeRow("therapy:manage");
  const changeable = (row: TherapyNoteTemplateRow) =>
    row.organizationId !== null && canChange(row.facilityId);
  const place = useNewRowPlace("therapy:manage");
  const buildingName = useBuildingName();
  const queryClient = useQueryClient();
  const [discipline, setDiscipline] = useState(ANY);
  const [editing, setEditing] = useState<TherapyNoteTemplateRow | null>(null);
  const [adding, setAdding] = useState(false);
  const [retiring, setRetiring] = useState<TherapyNoteTemplateRow | null>(null);
  const { q, retired: showRetired } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const { text: search, change: setSearch } = useSearchParamText(q, (next) => patch({ q: next }));
  const setShowRetired = (next: boolean) => patch({ retired: next ? true : undefined });

  const templates = useQuery(
    orpc.care.therapy.noteTemplates.queryOptions({
      input: {
        includeInactive: showRetired,
        ...(discipline === ANY ? {} : { discipline }),
      },
    }),
  );
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.care.therapy.noteTemplates.key() });

  const save = useMutation(
    orpc.care.therapy.saveNoteTemplate.mutationOptions({
      onSuccess: async () => {
        toast.success("Template saved");
        setAdding(false);
        setEditing(null);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const retire = useMutation(
    orpc.care.therapy.deactivateNoteTemplate.mutationOptions({
      onSuccess: async () => {
        toast.success("Template switched off");
        setRetiring(null);
        await invalidate();
      },
      onError: fail,
    }),
  );

  // Undo: only the fields the save shape requires travel (plus the section for a section template);
  // the rest keeps what is stored.
  const switchOn = (template: TherapyNoteTemplateRow) =>
    save.mutate({
      templateId: template.id,
      name: template.name,
      kind: template.kind,
      sectionKey: THERAPY_NOTE_SECTION_KEYS.find((key) => key === template.sectionKey),
      isActive: true,
    });

  const needle = search.trim().toLowerCase();
  const rows = (templates.data ?? []).filter(
    (row) =>
      !needle ||
      row.name.toLowerCase().includes(needle) ||
      (row.noteType ?? "").toLowerCase().includes(needle),
  );
  const open = adding || editing !== null;

  return (
    <>
      <PageHeader
        title="Note templates"
        meta="The opening a clinician starts a therapy note from, per discipline and note type."
      />

      <div className="flex flex-wrap items-center gap-3">
        <SearchField value={search} onChange={setSearch} placeholder="Search templates" />
        <Select onValueChange={setDiscipline} value={discipline}>
          <SelectTrigger aria-label="Discipline" className="w-52">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Every discipline</SelectItem>
            {THERAPY_DISCIPLINES.map((row) => (
              <SelectItem key={row.value} value={row.value}>
                {row.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Switch checked={showRetired} id="note-show-off" onCheckedChange={setShowRetired} />
          <Label htmlFor="note-show-off">Show switched-off templates</Label>
        </div>
      </div>

      <RecordCard
        addLabel="Add template"
        canWrite={canManage && (place.everyBuilding || place.buildings.length > 0)}
        count={templates.data ? rows.length : undefined}
        onAdd={() => setAdding(true)}
        title="Templates"
      >
        {templates.isPending ? (
          <RowsSkeleton />
        ) : templates.isError ? (
          <ReadFailed
            message="The templates could not be loaded."
            error={templates.error}
            retrying={templates.isFetching}
            onRetry={() => void templates.refetch()}
          />
        ) : rows.length === 0 ? (
          needle ? (
            <NoMatches search={search} onClear={() => setSearch("")} />
          ) : (
            <EmptyRows text="No templates yet. Write the opening an evaluation should start from and every clinician gets it." />
          )
        ) : (
          <ul className="divide-y">
            {rows.map((template) => (
              <RecordRow
                key={template.id}
                meta={[
                  sectionLabel(template),
                  disciplineLabel(template.discipline),
                  template.noteType,
                  template.facilityId ? `${buildingName(template.facilityId)} only` : null,
                ]
                  .filter(Boolean)
                  .join(", ")}
                title={
                  <span className="flex items-center gap-2">
                    {template.name}
                    {template.isActive ? null : <Badge variant="outline">Off</Badge>}
                    {template.organizationId ? null : <Badge variant="secondary">Standard</Badge>}
                  </span>
                }
                actions={
                  canManage && changeable(template) ? (
                    <RowActions
                      modal={false}
                      label={template.name}
                      primary={{ label: "Edit", onSelect: () => setEditing(template) }}
                      items={[
                        template.isActive
                          ? { label: "Switch off", onSelect: () => setRetiring(template) }
                          : { label: "Switch on", onSelect: () => switchOn(template) },
                      ]}
                    />
                  ) : canManage && !template.facilityId ? (
                    <SharedRowNote reason={template.organizationId ? undefined : STANDARD} />
                  ) : null
                }
              />
            ))}
          </ul>
        )}
      </RecordCard>

      <Dialog
        onOpenChange={(next) => {
          if (!next) {
            setAdding(false);
            setEditing(null);
          }
        }}
        open={open}
      >
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit template" : "Add template"}</DialogTitle>
          </DialogHeader>
          {open ? (
            <TherapyNoteTemplateForm
              key={editing?.id ?? "new"}
              facilities={place.buildings}
              everyBuilding={place.everyBuilding}
              startAt={place.everyBuilding ? null : place.startAt}
              onSave={(values) =>
                save.mutate(editing ? { ...values, templateId: editing.id } : values)
              }
              pending={save.isPending}
              template={editing}
              onCancel={() => {
                setAdding(false);
                setEditing(null);
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog open={retiring !== null} onOpenChange={(next) => !next && setRetiring(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch off “{retiring?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Nobody can start a new note from it. Notes already written keep their own copy of the
              wording, which is why it is switched off rather than deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep template</AlertDialogCancel>
            <AlertDialogAction
              disabled={retire.isPending}
              onClick={() => retiring && retire.mutate({ templateId: retiring.id })}
            >
              Switch off template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
